import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import constraintProfilesStore from 'shared/store/constraintProfilesStore';
import constraintsStore from 'shared/store/constraintsStore';

/**
 * Apply the per-mode default profile (or fall back to predefiniti) when the
 * user is on a FRESH CHAT — i.e. the `/assistant` route with no thread id —
 * and the catalog is loaded.
 *
 * Why this is its own hook and not in `useConstraintsChangeDetection`:
 *
 *   - For a thread URL the source of truth is the thread's metadata. The
 *     reconciliation in `useConstraintsChangeDetection` handles that.
 *
 *   - For `/assistant` there is no metadata to reconcile against; the user
 *     has nothing applied until WE apply a default. Conflating that with
 *     reconciliation muddles two different concerns.
 *
 * The prior implementation in `ActivityPanelContainer` fired whenever
 * `currentThreadId && !workingDocument` was true — including the brief window
 * during a thread URL load before the SDK delivered metadata. That made the
 * bootstrap race the reconciliation (silent sync of the default profile
 * could land first and overwrite the thread's stored constraints with the
 * default). Gating strictly on "no thread URL" removes that race.
 *
 * Mode/`currentThreadId` deps: re-fire on new chat (URL goes thread → undefined)
 * and on mode changes (which can happen via setMode from a deeper code path —
 * still defensive, since the typical flow doesn't change mode without a
 * thread).
 */
export const useFreshChatBootstrap = (): void => {
  const urlThreadId = useParams().threadId;
  const sessionLoaded = constraintProfilesStore((s) => s.loaded);
  const mode = constraintsStore((s) => s.mode);

  useEffect(() => {
    if (!sessionLoaded) return;
    if (urlThreadId) return; // only bootstrap for /assistant
    if (mode !== 'builder' && mode !== 'rebalancing') return;

    // /assistant is always a fresh start: apply the per-mode default profile
    // unconditionally (or fall back to Predefiniti if no default exists). No
    // carry-over from the prior thread — whatever profile (or lack thereof)
    // was on the thread the user just navigated away from is irrelevant; the
    // user clicked "Nuova chat" specifically to start clean. applyProfile /
    // applyPredefiniti both reset workingDocument and agentChangedPaths in
    // one transaction, so any prior-thread leftovers are overwritten here.
    //
    // Wipe `workingDocument` BEFORE applying the default profile so the
    // preservePortfolioFields call inside applyProfile / applyPredefiniti has
    // nothing to carry over. Without this, a rebalancing thread's
    // selected_list_ids / list_products / portfolio_products would leak into
    // the new builder chat (the prior thread's lists may not even exist in
    // builder context, and the user expects Nuova chat to start blank).
    constraintsStore.setState({ workingDocument: null });

    const defaultProfile = constraintProfilesStore
      .getState()
      .profilesByMode[mode]
      .find((p) => p.is_default);
    if (defaultProfile) {
      constraintsStore.getState().applyProfile(defaultProfile);
    } else {
      constraintsStore.getState().applyPredefiniti();
    }
  }, [sessionLoaded, mode, urlThreadId]);
};
